"use client";

import { Fragment, useRef, useState } from "react";
import { GroqImage } from "@/helpers/Image";
import IQCLogo from "@/components/global/ui/IQCLogo";
import { CompetitorComparisonRow } from "@/sanity/pages/PageCompetitorTemplate";
import { noWidow } from "./richtext";

const IQ_GLOW =
  "shadow-[inset_0.375rem_-2rem_4.5rem_rgba(143,237,184,0.15),inset_1.75rem_-8.125rem_8.25rem_rgba(143,237,184,0.13),inset_3.875rem_-18.25rem_11.125rem_rgba(143,237,184,0.08),inset_6.875rem_-32.375rem_13.25rem_rgba(143,237,184,0.02)]";

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-[2.5rem] w-[2.5rem] shrink-0">
      <path
        d="M10 1.25C8.26941 1.25 6.57769 1.76318 5.13876 2.72464C3.69983 3.6861 2.57832 5.05267 1.91605 6.65152C1.25378 8.25037 1.0805 10.0097 1.41812 11.707C1.75575 13.4044 2.5891 14.9635 3.81281 16.1872C5.03652 17.4109 6.59562 18.2442 8.29296 18.5819C9.99029 18.9195 11.7496 18.7462 13.3485 18.0839C14.9473 17.4217 16.3139 16.3002 17.2754 14.8612C18.2368 13.4223 18.75 11.7306 18.75 10C18.75 7.67936 17.8281 5.45376 16.1872 3.81282C14.5462 2.17187 12.3206 1.25 10 1.25ZM8.75 13.4942L5.625 10.3692L6.61912 9.375L8.75 11.5058L13.3812 6.875L14.3786 7.86619L8.75 13.4942Z"
        fill="#8FEDB8"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden className="h-[2.5rem] w-[2.5rem] shrink-0">
      <path
        d="M10 1.25C5.125 1.25 1.25 5.125 1.25 10C1.25 14.875 5.125 18.75 10 18.75C14.875 18.75 18.75 14.875 18.75 10C18.75 5.125 14.875 1.25 10 1.25ZM13.375 14.375L10 11L6.625 14.375L5.625 13.375L9 10L5.625 6.625L6.625 5.625L10 9L13.375 5.625L14.375 6.625L11 10L14.375 13.375L13.375 14.375Z"
        fill="#E52C2C"
      />
    </svg>
  );
}

function RowLabel({ row }: { row: CompetitorComparisonRow }) {
  return (
    <div className="flex items-center gap-[1.25rem]">
      {row.icon && <GroqImage src={row.icon} alt="" quality={100} className="!h-[2.5rem] !w-[2.5rem] shrink-0 object-contain" />}
      <p className="mb-0 font-geist text-[2rem] font-medium leading-[1.4] text-black">{row.label}</p>
    </div>
  );
}

function IqCard({ rows, className }: { rows: CompetitorComparisonRow[]; className?: string }) {
  return (
    <div
      className={`overflow-hidden rounded-[1rem] border border-[#8fedb8] bg-[rgba(22,50,43,0.94)] pt-[1.125rem] pb-[2.125rem] backdrop-blur-[11.375rem] ${IQ_GLOW} ${className ?? ""}`}>
      <div className="flex items-center border-b border-white/10 px-[4.5rem] py-[3.375rem]">
        <IQCLogo fill="white" width="18rem" height="3.33rem" />
      </div>
      {rows.map((row, index) => (
        <div
          key={row._key ?? index}
          className={`flex items-center gap-[2.25rem] px-[4rem] ${index < rows.length - 1 ? "h-[6.375rem] border-b border-white/10" : "py-[1.875rem]"}`}>
          <CheckIcon />
          <p className="mb-0 font-geist text-[2rem] font-medium leading-[1.3] text-white">{row.iqValue}</p>
        </div>
      ))}
    </div>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12.75 10.625" fill="none" aria-hidden className={className}>
      <path
        d="M7.4375 0L6.67781 0.740031L10.7047 4.78125H0V5.84375H10.7047L6.67781 9.86691L7.4375 10.625L12.75 5.3125L7.4375 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MobileComparisonTable({ rows, competitorLabel }: { rows: CompetitorComparisonRow[]; competitorLabel?: string }) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const last = rows.length - 1;
  const fullHeight = { gridRow: `1 / span ${rows.length + 1}` };

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
  };
  const scrollTo = (end: boolean) => {
    const el = scrollerRef.current;
    el?.scrollTo({ left: end ? el.scrollWidth : 0, behavior: "smooth" });
  };

  return (
    <div className="flex w-full flex-col gap-[2.25rem] text-left lg:hidden">
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="w-full snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="grid w-max grid-cols-[18rem_28.75rem_max(28.75rem,calc(100vw-21.75rem))] grid-rows-[7rem] auto-rows-[minmax(10rem,auto)]">
          <div
            aria-hidden
            className="sticky left-0 z-10 col-start-1 border border-[#e6e6e6] bg-white shadow-[0.75rem_0_1.5rem_-0.5rem_rgba(0,0,0,0.08)]"
            style={fullHeight}
          />
          <div aria-hidden className={`col-start-2 border-x border-[#8fedb8] bg-[rgba(22,50,43,0.94)] ${IQ_GLOW}`} style={fullHeight} />
          <div aria-hidden className="col-start-3 border border-black/10 bg-white" style={fullHeight} />

          <div className="sticky left-0 z-20 col-start-1 row-start-1 border-b border-black/10" />
          <div className="relative col-start-2 row-start-1 flex snap-start items-center border-b border-white/10 pr-[1.5rem] pl-[2.75rem] [scroll-margin-left:18rem]">
            <IQCLogo fill="white" width="13.5rem" height="2.5rem" />
          </div>
          <div className="relative col-start-3 row-start-1 flex snap-end items-center border-b border-black/10 pr-[1.5rem] pl-[2rem]">
            {competitorLabel && <p className="mb-0 font-geist text-[2rem] leading-[1.3] font-medium text-black/80">{competitorLabel}</p>}
          </div>

          {rows.map((row, index) => (
            <Fragment key={row._key ?? index}>
              <div
                className={`sticky left-0 z-20 col-start-1 flex items-center gap-[1.25rem] py-[1.5rem] pr-[1.25rem] pl-[2rem] ${index < last ? "border-b border-black/[0.08]" : ""}`}
                style={{ gridRow: index + 2 }}>
                {row.icon && <GroqImage src={row.icon} alt="" quality={100} className="!h-[2.5rem] !w-[2.5rem] shrink-0 object-contain opacity-60" />}
                <p className="mb-0 font-geist text-[1.75rem] leading-[1.3] font-medium text-black/80">{noWidow(row.label)}</p>
              </div>
              <div
                className={`relative col-start-2 flex items-center gap-[1.25rem] py-[1.5rem] pr-[1.75rem] pl-[2rem] ${index < last ? "border-b border-white/10" : ""}`}
                style={{ gridRow: index + 2 }}>
                <CheckIcon />
                <p className="mb-0 font-geist text-[2rem] leading-[1.4] font-medium text-white [text-wrap:balance]">{noWidow(row.iqValue)}</p>
              </div>
              <div
                className={`relative col-start-3 flex items-center gap-[1.25rem] py-[1.5rem] pr-[1.75rem] pl-[2rem] ${index < last ? "border-b border-black/[0.08]" : ""}`}
                style={{ gridRow: index + 2 }}>
                <CloseIcon />
                <p className="mb-0 font-geist text-[2rem] leading-[1.4] text-black/60 [text-wrap:balance]">{noWidow(row.competitorValue)}</p>
              </div>
            </Fragment>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-[1.875rem]">
        <button
          type="button"
          onClick={() => scrollTo(false)}
          aria-label="Previous"
          className={`flex h-[5.125rem] w-[5.125rem] shrink-0 items-center justify-center rounded-full transition-all duration-200 ${progress > 0.5 ? "bg-black text-white" : "cursor-default bg-black/10 text-black opacity-50"}`}>
          <ArrowIcon className="h-[2rem] w-[2rem] rotate-180" />
        </button>
        <div className="h-[0.5rem] flex-1 overflow-hidden rounded-full bg-black/15">
          <div className="h-full rounded-full bg-black transition-[width] duration-150" style={{ width: `${33 + 67 * progress}%` }} />
        </div>
        <button
          type="button"
          onClick={() => scrollTo(true)}
          aria-label="Next"
          className={`flex h-[5.125rem] w-[5.125rem] shrink-0 items-center justify-center rounded-full transition-all duration-200 ${progress <= 0.5 ? "bg-black text-white" : "cursor-default bg-black/10 text-black opacity-50"}`}>
          <ArrowIcon className="h-[2rem] w-[2rem]" />
        </button>
      </div>
    </div>
  );
}

export default function ComparisonTable({ rows, competitorLabel }: { rows: CompetitorComparisonRow[]; competitorLabel?: string }) {
  return (
    <>
      <div className="relative mt-[10.5rem] w-[min(138.5rem,100%)] rounded-[0.375rem] bg-[rgba(41,58,59,0.1)] py-[0.5rem] pr-[0.5rem] pl-[0.375rem] max-lg:hidden">
        {competitorLabel && (
          <p className="absolute top-[-7.875rem] right-[0.5rem] left-[80.5rem] mb-0 px-[4.5rem] py-[3.375rem] font-geist text-[3.25rem] leading-[1.3] font-semibold text-black [text-box:trim-both_cap_alphabetic]">
            {competitorLabel}
          </p>
        )}
        <div className="rounded-[0.125rem] bg-white">
          {rows.map((row, index) => (
            <div
              key={row._key ?? index}
              className={`flex items-center justify-between py-[1.75rem] pr-[4rem] pl-[2.5rem] ${index < rows.length - 1 ? "border-b border-black/10" : ""}`}>
              <RowLabel row={row} />
              <div className="flex w-[min(53.75rem,100%)] items-center gap-[2.25rem] px-[4rem]">
                <CloseIcon />
                <p className="mb-0 font-geist text-[2rem] leading-[1.4] text-black/60">{row.competitorValue}</p>
              </div>
            </div>
          ))}
        </div>
        <IqCard
          rows={rows}
          className="absolute top-[-11.125rem] left-[26.25rem] w-[min(53.75rem,100%)]"
        />
      </div>
      <MobileComparisonTable rows={rows} competitorLabel={competitorLabel} />
    </>
  );
}
